import { useState, useRef, useEffect } from "react";
import { User, Star, Settings, ShoppingBag, LogOut, Moon, Sun, X, Loader2, ImageOff, Award, Phone, Mail, MapPin, Calendar, Plus, Trash2, Home, Building, Edit2, Camera, Check, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { removeToken } from "../utils/tokenStorage";
import { validateCustomerName } from "../utils/nameValidator";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formaBase } from "../features/pedidos/lib/formasDaLoja";
import { rotuloPagamento } from "../features/pedidos/lib/pagamentoRotulo";
import { comPulseira } from "@/features/cliente-sessao";

const API = '/api';

// Formatar telefone (XX) 9.XXXX-XXXX
const formatTelefone = (value) => {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 2) return `(${numbers}`;
  if (numbers.length <= 3) return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)})${numbers.slice(2, 3)}.${numbers.slice(3)}`;
  if (numbers.length <= 11) return `(${numbers.slice(0, 2)})${numbers.slice(2, 3)}.${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  return `(${numbers.slice(0, 2)})${numbers.slice(2, 3)}.${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

// Formatar CPF XXX.XXX.XXX-XX
const formatCPF = (value) => {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
  if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
  return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
};

// Formatar Data XX/XX/XXXX
const formatData = (value) => {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
};

// Normaliza data pra DD/MM/AAAA, aceitando ISO (YYYY-MM-DD) ou digitação livre
const normalizeDataBR = (value) => {
  if (!value) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return formatData(value);
};

const isEmptyField = (v) => !v || !String(v).trim();
const MISSING_INPUT_CLS = "border-amber-500/60 ring-2 ring-amber-500/25 bg-amber-500/[0.04] focus-visible:ring-amber-500/40";

// Label com aviso "preencher" em âmbar quando o campo está vazio
function LabelWithHint({ children, empty }) {
  return (
    <Label className="text-xs flex items-center gap-1.5">
      <span>{children}</span>
      {empty && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
          <AlertCircle className="w-3 h-3" />
          preencher
        </span>
      )}
    </Label>
  );
}

// Formatar CEP XXXXX-XXX
const formatCEP = (value) => {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 5) return numbers;
  return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
};

// Modal de Edição de Conta (Réplica do popup de Clientes)
export function EditAccountModal({ isOpen, onClose, client, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [genero, setGenero] = useState("");
  const [fotoPreview, setFotoPreview] = useState("");
  const [endereco, setEndereco] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cep, setCep] = useState("");
  
  // Estados para múltiplos endereços
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [newAddr, setNewAddr] = useState({ label: 'Casa', endereco: '', numero: '', complemento: '', bairro: '', cep: '' });

  useEffect(() => {
    if (client && isOpen) {
      setNome(client.nome || "");
      setTelefone(formatTelefone(client.telefone || ""));
      setEmail(client.email || "");
      setCpf(formatCPF(client.cpf || ""));
      setDataNascimento(normalizeDataBR(client.data_nascimento || ""));
      setGenero(client.genero || "");
      setFotoPreview(client.foto || "");
      setEndereco(client.endereco || "");
      setNumero(client.numero || "");
      setComplemento(client.complemento || "");
      setBairro(client.bairro || "");
      setCep(formatCEP(client.cep || ""));
      fetchAddresses();
    }
  }, [client, isOpen]);
  
  const fetchAddresses = async () => {
    if (!client?.id) return;
    setLoadingAddresses(true);
    try {
      const response = await axios.get(`${API}/client-addresses/${client.id}`, comPulseira());
      setAddresses(response.data);
    } catch (error) {
      console.error("Erro ao carregar endereços:", error);
    } finally {
      setLoadingAddresses(false);
    }
  };
  
  const handleSaveNewAddress = async () => {
    if (!newAddr.endereco || !newAddr.bairro) {
      toast.error("Preencha o endereço e bairro");
      return;
    }
    try {
      const response = await axios.post(`${API}/client-addresses`, {
        client_id: client.id,
        ...newAddr,
        is_default: addresses.length === 0
      }, comPulseira());
      setAddresses(prev => [response.data, ...prev]);
      setShowNewAddress(false);
      setNewAddr({ label: 'Casa', endereco: '', numero: '', complemento: '', bairro: '', cep: '' });
      toast.success("Endereço adicionado!");
    } catch (error) {
      toast.error("Erro ao salvar endereço");
    }
  };
  
  const handleUpdateAddress = async () => {
    if (!editingAddress) return;
    try {
      const response = await axios.put(`${API}/client-addresses/${editingAddress.id}`, editingAddress, comPulseira());
      setAddresses(prev => prev.map(a => a.id === editingAddress.id ? response.data : a));
      setEditingAddress(null);
      toast.success("Endereço atualizado!");
    } catch (error) {
      toast.error("Erro ao atualizar endereço");
    }
  };
  
  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Deseja excluir este endereço?")) return;
    try {
      await axios.delete(`${API}/client-addresses/${addressId}`, comPulseira());
      setAddresses(prev => prev.filter(a => a.id !== addressId));
      toast.success("Endereço excluído!");
    } catch (error) {
      toast.error("Erro ao excluir endereço");
    }
  };
  
  const handleSetDefault = async (addressId) => {
    try {
      await axios.put(`${API}/client-addresses/${addressId}`, { is_default: true }, comPulseira());
      setAddresses(prev => prev.map(a => ({ ...a, is_default: a.id === addressId })));
      toast.success("Endereço padrão definido!");
    } catch (error) {
      toast.error("Erro ao definir endereço padrão");
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!nome.trim()) {
      toast.error("Informe seu nome");
      return;
    }
    const nameCheck = validateCustomerName(nome);
    if (!nameCheck.valid) {
      toast.error(nameCheck.error);
      return;
    }
    if (!telefone.trim()) {
      toast.error("Informe seu telefone");
      return;
    }

    setLoading(true);

    try {
      const clienteData = {
        nome: nameCheck.name,
        telefone,
        email,
        cpf,
        data_nascimento: dataNascimento,
        genero,
        foto: fotoPreview,
        endereco,
        numero,
        complemento,
        bairro,
        cep,
        // Manter pontuação original - não pode editar
        pontuacao: client?.pontuacao || 0,
      };

      const response = await axios.put(`${API}/public/clientes/${client.id}`, clienteData, comPulseira());
      localStorage.setItem("client", JSON.stringify(response.data));
      onUpdate(response.data);
      toast.success("Conta atualizada com sucesso!");
      onClose();
    } catch (error) {
      console.error("Erro ao atualizar conta:", error);
      toast.error("Erro ao atualizar conta");
    } finally {
      setLoading(false);
    }
  };

  const primeiroNome = (nome || client?.nome || "").split(" ")[0] || "Você";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] p-0 overflow-hidden flex flex-col">
        {/* ===== HEADER slim — título discreto, avatar em destaque logo abaixo ===== */}
        <DialogHeader className="p-0 space-y-0">
          <div className="px-6 pt-5 pb-3 border-b">
            <DialogTitle asChild>
              <h2 className="text-sm font-semibold text-foreground">Minha Conta</h2>
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Atualize seus dados e endereços</p>
          </div>
          {/* Avatar centralizado */}
          <div className="flex justify-center pt-4 pb-1">
            <label className="cursor-pointer group relative">
              <div className="w-20 h-20 rounded-full ring-2 ring-primary/25 bg-muted overflow-hidden shadow-md flex items-center justify-center">
                {fotoPreview ? (
                  <img src={fotoPreview} alt="Foto do perfil" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-9 h-9 text-muted-foreground" />
                )}
              </div>
              <div className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md border-2 border-background group-hover:scale-110 transition-transform">
                <Camera className="w-3.5 h-3.5" />
              </div>
              <input type="file" accept="image/*" onChange={handleFotoChange} className="hidden" />
            </label>
          </div>
          <div className="text-center pb-3 px-6">
            <p className="font-bold text-base">{primeiroNome}</p>
            {telefone && <p className="text-xs text-muted-foreground">{telefone}</p>}
          </div>
        </DialogHeader>

        {/* ===== TABS ===== */}
        <Tabs defaultValue="perfil" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 grid w-auto grid-cols-2 bg-muted/60">
            <TabsTrigger value="perfil" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <User className="w-3.5 h-3.5 mr-1.5" />
              Perfil
            </TabsTrigger>
            <TabsTrigger value="enderecos" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MapPin className="w-3.5 h-3.5 mr-1.5" />
              Endereços {addresses.length > 0 && `(${addresses.length})`}
            </TabsTrigger>
          </TabsList>

          {/* ========= TAB PERFIL ========= */}
          <TabsContent value="perfil" className="flex-1 overflow-y-auto px-6 py-4 mt-0 data-[state=inactive]:hidden space-y-4">
            {/* Aviso de campos pendentes */}
            {(() => {
              const missing = [nome, dataNascimento, genero, telefone, cpf, email].filter(isEmptyField).length;
              if (missing === 0) return null;
              return (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/[0.08] px-3 py-2 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {missing === 1
                      ? "Falta 1 informação pra completar seu cadastro."
                      : `Faltam ${missing} informações pra completar seu cadastro.`}
                  </span>
                </div>
              );
            })()}

            {/* Info Básica */}
            <section className="rounded-xl border p-4 space-y-3 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-bold">Informações básicas</h3>
              </div>
              <div>
                <LabelWithHint empty={isEmptyField(nome)}>Nome completo *</LabelWithHint>
                <Input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Seu nome completo"
                  className={`mt-1 ${isEmptyField(nome) ? MISSING_INPUT_CLS : ""}`}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <LabelWithHint empty={isEmptyField(dataNascimento)}>Nascimento</LabelWithHint>
                  <Input
                    value={dataNascimento}
                    onChange={(e) => setDataNascimento(formatData(e.target.value))}
                    placeholder="DD/MM/AAAA"
                    className={`mt-1 ${isEmptyField(dataNascimento) ? MISSING_INPUT_CLS : ""}`}
                    maxLength={10}
                  />
                </div>
                <div>
                  <LabelWithHint empty={isEmptyField(genero)}>Gênero</LabelWithHint>
                  <div className={`mt-1 flex rounded-lg border overflow-hidden h-9 ${isEmptyField(genero) ? "border-amber-500/60 ring-2 ring-amber-500/25 bg-amber-500/[0.04]" : ""}`}>
                    <button
                      type="button"
                      onClick={() => setGenero("masculino")}
                      className={`flex-1 text-xs font-medium transition-colors ${genero === "masculino" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                    >
                      Masculino
                    </button>
                    <button
                      type="button"
                      onClick={() => setGenero("feminino")}
                      className={`flex-1 text-xs font-medium transition-colors border-l ${genero === "feminino" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                    >
                      Feminino
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Contato */}
            <section className="rounded-xl border p-4 space-y-3 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <Phone className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-bold">Contato</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <LabelWithHint empty={isEmptyField(telefone)}>Telefone *</LabelWithHint>
                  <Input
                    value={telefone}
                    onChange={(e) => setTelefone(formatTelefone(e.target.value))}
                    placeholder="(XX)9.XXXX-XXXX"
                    className={`mt-1 ${isEmptyField(telefone) ? MISSING_INPUT_CLS : ""}`}
                    maxLength={16}
                  />
                </div>
                <div>
                  <LabelWithHint empty={isEmptyField(cpf)}>CPF</LabelWithHint>
                  <Input
                    value={cpf}
                    onChange={(e) => setCpf(formatCPF(e.target.value))}
                    placeholder="XXX.XXX.XXX-XX"
                    className={`mt-1 ${isEmptyField(cpf) ? MISSING_INPUT_CLS : ""}`}
                    maxLength={14}
                  />
                </div>
              </div>
              <div>
                <LabelWithHint empty={isEmptyField(email)}>Email</LabelWithHint>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu.email@exemplo.com"
                  className={`mt-1 ${isEmptyField(email) ? MISSING_INPUT_CLS : ""}`}
                />
              </div>
            </section>
          </TabsContent>

          {/* ========= TAB ENDEREÇOS ========= */}
          <TabsContent value="enderecos" className="flex-1 overflow-y-auto px-6 py-4 mt-0 data-[state=inactive]:hidden space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {addresses.length === 0 ? "Nenhum endereço cadastrado" : `${addresses.length} endereço${addresses.length === 1 ? "" : "s"} cadastrado${addresses.length === 1 ? "" : "s"}`}
              </p>
              {!showNewAddress && !editingAddress && (
                <Button size="sm" onClick={() => setShowNewAddress(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1">
                  <Plus className="w-4 h-4" />
                  Novo
                </Button>
              )}
            </div>

            {loadingAddresses ? (
              <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto" /></div>
            ) : addresses.length === 0 && !showNewAddress ? (
              <div className="text-center py-10 border-2 border-dashed rounded-xl">
                <MapPin className="w-10 h-10 mx-auto mb-2 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground mb-3">Nenhum endereço cadastrado</p>
                <Button size="sm" onClick={() => setShowNewAddress(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar endereço
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {addresses.map(addr => (
                  <div
                    key={addr.id}
                    className={`group p-3 rounded-xl border-2 flex items-start gap-3 transition-colors ${addr.is_default ? 'border-primary bg-primary/[0.04]' : 'border-border hover:border-primary/40'}`}
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${addr.is_default ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                      {addr.label === 'Casa' ? <Home className="w-5 h-5" /> : <Building className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm">{addr.label}</span>
                        {addr.is_default && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
                            Padrão
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {addr.endereco}, {addr.numero} {addr.complemento && `- ${addr.complemento}`}
                      </p>
                      <p className="text-xs text-muted-foreground">{addr.bairro}{addr.cep ? ` · ${addr.cep}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {!addr.is_default && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => handleSetDefault(addr.id)} title="Definir como padrão">
                          <Star className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => setEditingAddress(addr)} title="Editar">
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => handleDeleteAddress(addr.id)} title="Excluir">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Form Novo Endereço */}
            {showNewAddress && (
              <AddressForm
                title="Novo endereço"
                data={newAddr}
                onChange={setNewAddr}
                onSave={handleSaveNewAddress}
                onCancel={() => setShowNewAddress(false)}
                saveLabel="Salvar endereço"
              />
            )}

            {/* Form Editar */}
            {editingAddress && (
              <AddressForm
                title="Editar endereço"
                data={editingAddress}
                onChange={setEditingAddress}
                onSave={handleUpdateAddress}
                onCancel={() => setEditingAddress(null)}
                saveLabel="Atualizar"
              />
            )}
          </TabsContent>
        </Tabs>

        {/* ===== FOOTER STICKY ===== */}
        <div className="border-t bg-muted/30 px-6 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Salvar Alterações
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Form reutilizável de endereço (novo + editar). Mesma UI pros dois casos.
function AddressForm({ title, data, onChange, onSave, onCancel, saveLabel }) {
  return (
    <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-primary">{title}</h4>
        <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Tipo */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[
          { key: 'Casa', icon: Home, label: 'Casa' },
          { key: 'Trabalho', icon: Building, label: 'Trabalho' },
        ].map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange(prev => ({ ...prev, label: key }))}
            className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border-2 text-xs font-semibold transition-colors ${data.label === key ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40'}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>
      {/* Campos */}
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-8">
          <Label className="text-[10px]">Rua / Logradouro</Label>
          <Input placeholder="Ex: Rua José Cândido..." value={data.endereco || ''} onChange={e => onChange(prev => ({ ...prev, endereco: e.target.value }))} className="h-9 mt-0.5" />
        </div>
        <div className="col-span-4">
          <Label className="text-[10px]">Número</Label>
          <Input placeholder="320" value={data.numero || ''} onChange={e => onChange(prev => ({ ...prev, numero: e.target.value }))} className="h-9 mt-0.5" />
        </div>
        <div className="col-span-12">
          <Label className="text-[10px]">Complemento (opcional)</Label>
          <Input placeholder="Bloco, apto, referência..." value={data.complemento || ''} onChange={e => onChange(prev => ({ ...prev, complemento: e.target.value }))} className="h-9 mt-0.5" />
        </div>
        <div className="col-span-7">
          <Label className="text-[10px]">Bairro</Label>
          <Input placeholder="Ex: Vila Rica" value={data.bairro || ''} onChange={e => onChange(prev => ({ ...prev, bairro: e.target.value }))} className="h-9 mt-0.5" />
        </div>
        <div className="col-span-5">
          <Label className="text-[10px]">CEP</Label>
          <Input placeholder="XXXXX-XXX" value={data.cep || ''} onChange={e => onChange(prev => ({ ...prev, cep: formatCEP(e.target.value) }))} maxLength={9} className="h-9 mt-0.5" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button size="sm" onClick={onSave} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Check className="w-3.5 h-3.5 mr-1" />
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}

// Modal de Pontuação
function PontuacaoModal({ isOpen, onClose, client }) {
  if (!isOpen) return null;

  const pontos = client?.pontuacao || 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 border">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold text-lg flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Minha Pontuação
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Star className="w-12 h-12 text-white" />
          </div>
          <p className="text-muted-foreground mb-2">Você tem</p>
          <p className="text-5xl font-bold text-primary mb-2">{pontos.toLocaleString('pt-BR')}</p>
          <p className="text-muted-foreground">pontos acumulados</p>
          
          <div className="mt-6 p-4 bg-muted rounded-lg text-left">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Como ganhar pontos?</strong><br />
              A cada compra você acumula pontos que podem ser trocados por descontos e benefícios exclusivos.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal de Meus Pedidos
function MeusPedidosModal({ isOpen, onClose, client, darkMode }) {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);

  useEffect(() => {
    if (isOpen && client) {
      fetchPedidos();
    }
    // Limpar seleção ao fechar
    if (!isOpen) {
      setPedidoSelecionado(null);
    }
  }, [isOpen, client]);

  const fetchPedidos = async () => {
    setLoading(true);
    try {
      // Rota do cliente (a /pedidos/cliente é do painel e exige login da loja:
      // aqui dava 401 e a lista vinha vazia)
      const response = await axios.get(`${API}/public/pedidos/cliente/${client.id}`);
      // Ordenar por data mais recente
      const pedidosOrdenados = (response.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setPedidos(pedidosOrdenados);
    } catch (error) {
      console.error("Erro ao carregar pedidos:", error);
      setPedidos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR") + " às " + date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const statusConfig = {
    agendado: { label: "Agendado", color: "bg-indigo-500", icon: "🗓️" },
    aguardando_aceite: { label: "Aguardando Aceite", color: "bg-yellow-500", icon: "⏳" },
    producao: { label: "Em Produção", color: "bg-orange-500", icon: "🍳" },
    pronto: { label: "Pronto", color: "bg-blue-500", icon: "✅" },
    na_bag: { label: "Na Bag", color: "bg-purple-500", icon: "📦" },
    em_rota: { label: "Em Rota", color: "bg-indigo-500", icon: "🛵" },
    concluido: { label: "Entregue", color: "bg-green-500", icon: "🎉" },
    cancelado: { label: "Cancelado", color: "bg-red-500", icon: "❌" },
    aguardando: { label: "Aguardando", color: "bg-gray-500", icon: "⏳" },
    transito: { label: "Em Trânsito", color: "bg-purple-500", icon: "🛵" },
  };

  // Formata "2026-07-05T19:00:00" como janela de 30min ("Sáb 5 · 19:00–19:30")
  // pra dar margem de erro no preparo/entrega.
  const fmtScheduledAt = (iso) => {
    if (!iso) return "";
    try {
      const [d, hm] = String(iso).split("T");
      const [y, mo, da] = d.split("-").map((n) => parseInt(n, 10));
      const [hh, mm] = (hm || "00:00").split(":");
      const dt = new Date(y, mo - 1, da);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
      let dayLbl;
      if (diff === 0) dayLbl = "Hoje";
      else if (diff === 1) dayLbl = "Amanhã";
      else {
        const wd = dt.toLocaleDateString("pt-BR", { weekday: "short" }).replace(/\.$/, "");
        dayLbl = `${wd.charAt(0).toUpperCase() + wd.slice(1)} ${da}`;
      }
      const startMinutes = parseInt(hh, 10) * 60 + parseInt(mm, 10);
      const endMinutes = startMinutes + 30;
      const eh = String(Math.floor(endMinutes / 60) % 24).padStart(2, "0");
      const em = String(endMinutes % 60).padStart(2, "0");
      return `${dayLbl} · ${hh}:${mm}–${eh}:${em}`;
    } catch { return iso; }
  };

  const formasPagamento = {
    pix: { label: "PIX", icon: "📱" },
    cartao: { label: "Cartão", icon: "💳" },
    dinheiro: { label: "Dinheiro", icon: "💵" },
  };

  if (!isOpen) return null;

  const t = {
    bg: darkMode ? 'bg-zinc-900' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textMuted: darkMode ? 'text-zinc-400' : 'text-gray-500',
    border: darkMode ? 'border-zinc-700' : 'border-gray-200',
    bgCard: darkMode ? 'bg-zinc-800' : 'bg-gray-50',
    bgHover: darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100',
  };

  // Visualização de detalhes do pedido
  if (pedidoSelecionado) {
    const status = statusConfig[pedidoSelecionado.status] || { label: pedidoSelecionado.status, color: 'bg-gray-500', icon: '📋' };
    // "dinheiro_<loja>" é código interno: vale o grupo (ícone) e o nome, nunca o código
    const pagamento = formasPagamento[formaBase(pedidoSelecionado.forma_pagamento)] || { label: rotuloPagamento(pedidoSelecionado.forma_pagamento), icon: '💰' };
    
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className={`relative ${t.bg} rounded-2xl shadow-2xl w-full max-w-2xl mx-4 border ${t.border} max-h-[90vh] overflow-hidden`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${t.border}`}>
            <button 
              onClick={() => setPedidoSelecionado(null)} 
              className={`text-sm flex items-center gap-1 ${t.textMuted} hover:text-primary`}
            >
              ← Voltar
            </button>
            <h2 className={`font-bold text-lg ${t.text}`}>
              Pedido {pedidoSelecionado.codigo}
            </h2>
            <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-700/50 ${t.textMuted}`}>
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="overflow-y-auto max-h-[70vh] p-4 space-y-4">
            {/* Status Badge */}
            <div className="flex items-center justify-center">
              <div className={`px-4 py-2 rounded-full text-white font-medium flex items-center gap-2 ${status.color}`}>
                <span className="text-lg">{status.icon}</span>
                {status.label}
              </div>
            </div>

            {/* Destaque de agendamento (só pra pedido agendado) */}
            {pedidoSelecionado.status === "agendado" && pedidoSelecionado.scheduled_at && (
              <div className="rounded-xl p-4 border-2 border-indigo-500/40 bg-indigo-500/[0.06] flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-indigo-500 text-white flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-indigo-600 dark:text-indigo-400">Entrega prevista</p>
                  <p className={`font-bold text-lg ${t.text}`}>{fmtScheduledAt(pedidoSelecionado.scheduled_at)}</p>
                  <p className={`text-xs ${t.textMuted} mt-0.5`}>
                    A entrega pode chegar entre o horário previsto e 30 min depois — o estabelecimento aprova seu agendamento e começa o preparo próximo do horário.
                  </p>
                </div>
              </div>
            )}

            {/* Info do Pedido */}
            <div className={`${t.bgCard} rounded-xl p-4 border ${t.border}`}>
              <h3 className={`font-semibold mb-3 ${t.text}`}>📋 Informações do Pedido</h3>
              <div className={`space-y-2 text-sm ${t.textMuted}`}>
                <p><strong>Data:</strong> {formatDate(pedidoSelecionado.created_at)}</p>
                <p><strong>Tipo:</strong> {pedidoSelecionado.tipo_entrega === 'delivery' ? '🛵 Entrega' : '🏪 Retirada'}</p>
                <p><strong>Pagamento:</strong> {pagamento.icon} {pagamento.label}</p>
                {pedidoSelecionado.troco_precisa && pedidoSelecionado.troco_valor && (
                  <p><strong>Troco para:</strong> R$ {pedidoSelecionado.troco_valor.toFixed(2).replace('.', ',')}</p>
                )}
              </div>
            </div>

            {/* Endereço de Entrega */}
            {pedidoSelecionado.tipo_entrega === 'delivery' && pedidoSelecionado.endereco_rua && (
              <div className={`${t.bgCard} rounded-xl p-4 border ${t.border}`}>
                <h3 className={`font-semibold mb-3 ${t.text}`}>📍 Endereço de Entrega</h3>
                <div className={`text-sm ${t.textMuted}`}>
                  <p>{pedidoSelecionado.endereco_rua}, {pedidoSelecionado.endereco_numero}</p>
                  {pedidoSelecionado.endereco_complemento && <p>{pedidoSelecionado.endereco_complemento}</p>}
                  <p>{pedidoSelecionado.endereco_bairro}</p>
                  {pedidoSelecionado.endereco_cep && <p>CEP: {pedidoSelecionado.endereco_cep}</p>}
                </div>
              </div>
            )}

            {/* Itens do Pedido */}
            <div className={`${t.bgCard} rounded-xl p-4 border ${t.border}`}>
              <h3 className={`font-semibold mb-3 ${t.text}`}>🛒 Itens do Pedido</h3>
              <div className="space-y-3">
                {pedidoSelecionado.items?.map((item, idx) => (
                  <div key={idx} className={`flex justify-between items-start pb-2 ${idx < pedidoSelecionado.items.length - 1 ? `border-b ${t.border}` : ''}`}>
                    <div className="flex-1">
                      <p className={`font-medium ${t.text}`}>{item.quantidade}x {item.nome}</p>
                      {item.observacao && (
                        <p className={`text-xs ${t.textMuted} mt-1`}>Obs: {item.observacao}</p>
                      )}
                    </div>
                    <p className={`font-medium ${t.text}`}>
                      R$ {((item.preco || 0) * (item.quantidade || 1)).toFixed(2).replace('.', ',')}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Observação */}
            {pedidoSelecionado.observacao && (
              <div className={`${t.bgCard} rounded-xl p-4 border ${t.border}`}>
                <h3 className={`font-semibold mb-2 ${t.text}`}>📝 Observação</h3>
                <p className={`text-sm ${t.textMuted}`}>{pedidoSelecionado.observacao}</p>
              </div>
            )}

            {/* Total */}
            <div className={`${t.bgCard} rounded-xl p-4 border-2 border-orange-500`}>
              <div className="space-y-2">
                {pedidoSelecionado.tipo_entrega === 'delivery' && (pedidoSelecionado.valor_entrega > 0) && (
                  <div className="flex justify-between text-sm">
                    <span className={t.textMuted}>Taxa de entrega:</span>
                    <span className={t.text}>R$ {(pedidoSelecionado.valor_entrega || 0).toFixed(2).replace('.', ',')}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className={`font-semibold ${t.text}`}>Total:</span>
                  <span className="font-bold text-xl text-primary">
                    R$ {(pedidoSelecionado.total || 0).toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
            </div>

            {/* Motivo de cancelamento */}
            {pedidoSelecionado.status === 'cancelado' && pedidoSelecionado.motivo_cancelamento && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <h3 className="font-semibold mb-2 text-red-500">❌ Motivo do Cancelamento</h3>
                <p className={`text-sm ${t.textMuted}`}>{pedidoSelecionado.motivo_cancelamento}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Lista de pedidos
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative ${t.bg} rounded-2xl shadow-2xl w-full max-w-2xl mx-4 border ${t.border} max-h-[90vh] overflow-hidden`}>
        <div className={`flex items-center justify-between p-4 border-b ${t.border}`}>
          <h2 className={`font-bold text-lg flex items-center gap-2 ${t.text}`}>
            <ShoppingBag className="w-5 h-5 text-primary" />
            Meus Pedidos
          </h2>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-700/50 ${t.textMuted}`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[70vh] p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className={t.textMuted}>Carregando pedidos...</p>
            </div>
          ) : pedidos.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className={`w-16 h-16 mx-auto mb-4 ${t.textMuted} opacity-50`} />
              <p className={t.textMuted}>Você ainda não fez nenhum pedido</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pedidos.map((pedido) => {
                const status = statusConfig[pedido.status] || { label: pedido.status, color: 'bg-gray-500', icon: '📋' };
                const isAgendado = pedido.status === 'agendado';
                return (
                  <div
                    key={pedido.id}
                    onClick={() => setPedidoSelecionado(pedido)}
                    className={`${isAgendado ? 'bg-indigo-500/[0.06] border-indigo-500/40 ring-1 ring-indigo-500/20' : `${t.bgCard} ${t.border}`} rounded-xl p-4 border cursor-pointer ${t.bgHover} transition-all overflow-hidden`}
                  >
                    {/* Banner topo — agendamento */}
                    {isAgendado && pedido.scheduled_at && (
                      <div className="-mx-4 -mt-4 mb-3 px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                        <Calendar className="w-3.5 h-3.5" />
                        Entrega prevista {fmtScheduledAt(pedido.scheduled_at)}
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className={`font-bold text-lg ${t.text}`}>{pedido.codigo}</span>
                        <p className={`text-sm ${t.textMuted}`}>{formatDate(pedido.created_at)}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-white text-sm font-medium flex items-center gap-1 ${status.color}`}>
                        <span>{status.icon}</span>
                        {status.label}
                      </div>
                    </div>
                    
                    <div className={`text-sm ${t.textMuted} mb-2`}>
                      {pedido.items?.slice(0, 3).map((item, idx) => (
                        <span key={idx}>
                          {item.quantidade}x {item.nome}
                          {idx < Math.min(pedido.items.length, 3) - 1 ? ', ' : ''}
                        </span>
                      ))}
                      {pedido.items?.length > 3 && (
                        <span className="text-primary"> +{pedido.items.length - 3} itens</span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${t.textMuted}`}>
                        {pedido.tipo_entrega === 'delivery' ? '🛵 Entrega' : '🏪 Retirada'}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-primary">
                          R$ {(pedido.total || 0).toFixed(2).replace('.', ',')}
                        </span>
                        <span className={`text-xs ${t.textMuted}`}>→</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente Principal - Menu de Perfil
export default function ProfileMenu({ client, onLogout, onClientUpdate, darkMode, onToggleTheme, onNavigateToPedidos }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showPontuacao, setShowPontuacao] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);
  const menuRef = useRef(null);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    removeToken();
    ['user', 'client', 'cliente_token', 'fornecedores', 'mesas', 'tenant_trial'].forEach(k => {
      try { localStorage.removeItem(k); } catch {}
    });
    onLogout();
    toast.success("Você saiu da sua conta");
    setIsOpen(false);
  };

  const handleNavigateToPedidos = () => {
    setIsOpen(false);
    if (onNavigateToPedidos) {
      onNavigateToPedidos();
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Botão do Perfil - Compacto (com touch-action + active state pra elimitar delay no mobile) */}
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="flex items-center gap-2 active:opacity-60 hover:opacity-80 touch-manipulation transition-opacity duration-100 cursor-pointer"
      >
        {client?.foto ? (
          <img 
            src={client.foto.startsWith('data:') || client.foto.startsWith('http') ? client.foto : `${process.env.REACT_APP_BACKEND_URL || ''}${client.foto}`} 
            alt={client.nome}
            className="w-8 h-8 rounded-full object-cover border-2 border-orange-500"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 items-center justify-center ${client?.foto ? 'hidden' : 'flex'}`}>
          <User className="w-4 h-4 text-white" />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute right-0 top-full mt-2 w-64 rounded-xl shadow-2xl border overflow-hidden z-50 ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
          {/* Header do Menu */}
          <div className={`p-4 border-b ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-3">
              {client?.foto ? (
                <img 
                  src={client.foto.startsWith('data:') || client.foto.startsWith('http') ? client.foto : `${process.env.REACT_APP_BACKEND_URL || ''}${client.foto}`} 
                  alt={client.nome} 
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 items-center justify-center ${client?.foto ? 'hidden' : 'flex'}`}>
                <User className={`w-6 h-6 text-white`} />
              </div>
              <div>
                <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{client?.nome}</p>
                <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{client?.telefone}</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Botão Pontuação removido em v1.5.251 — feature ainda não implementada
                (só cliente vê "0 pontos", confunde). O componente PontuacaoModal
                continua abaixo pra retomada rápida quando a feature ficar pronta. */}

            <button
              onClick={() => { setShowEditAccount(true); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}
            >
              <Settings className={`w-5 h-5 ${darkMode ? 'text-zinc-400' : 'text-gray-400'}`} />
              <span className={darkMode ? 'text-white' : 'text-gray-900'}>Minha Conta</span>
            </button>

            <button
              onClick={handleNavigateToPedidos}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}
            >
              <ShoppingBag className={`w-5 h-5 ${darkMode ? 'text-zinc-400' : 'text-gray-400'}`} />
              <span className={darkMode ? 'text-white' : 'text-gray-900'}>Meus Pedidos</span>
            </button>
          </div>

          {/* Theme Toggle */}
          <div className={`px-4 py-3 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {darkMode ? <Moon className={`w-5 h-5 ${darkMode ? 'text-zinc-400' : 'text-gray-400'}`} /> : <Sun className="w-5 h-5 text-yellow-500" />}
                <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>Modo {darkMode ? 'Escuro' : 'Claro'}</span>
              </div>
              <button
                onClick={onToggleTheme}
                className={`w-12 h-6 rounded-full transition-colors relative ${darkMode ? 'bg-orange-500' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow ${darkMode ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          {/* Logout */}
          <div className={`p-2 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-500/10 rounded-lg transition-colors text-red-400"
            >
              <LogOut className="w-5 h-5" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      )}

      {/* Modais — lazy mount: só renderiza quando o modal vai abrir. Antes ambos
          ficavam montados permanentemente e re-renderizavam a cada toggle do menu */}
      {showPontuacao && <PontuacaoModal isOpen={showPontuacao} onClose={() => setShowPontuacao(false)} client={client} />}
      {showEditAccount && <EditAccountModal isOpen={showEditAccount} onClose={() => setShowEditAccount(false)} client={client} onUpdate={onClientUpdate} />}
    </div>
  );
}
